/*
 * File: ui-meta.ts
 * File Created: 2024-03-18 09:33:33
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-01-16 16:33:44
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import {
    BaseUITag,
    EUITagRuleType,
    EUITagRuleUnion,
    EUITagTarget,
    IUITagInput,
    IUITagResult,
    UITag,
} from "./core";

// @uiMeta - apply additional meta to form element or table cell.
// it should be used in another tags to have real meanings.

// For iv-quick,
// basic: It will show up in basic mode. default to false.
// detail: It will show up in detail node. default to true.

@UITag.set()
export class uiMeta extends BaseUITag {
    appliedTo: EUITagTarget[] = [
        EUITagTarget.TableHeaderCellContainer,
        EUITagTarget.TableHeaderCell,
        EUITagTarget.TableBodyCellContainer,
        EUITagTarget.TableBodyCell,
        EUITagTarget.TableRow,
        EUITagTarget.TableSearch,
        EUITagTarget.FormElement,
    ];
    ruleTypes: EUITagRuleUnion[] = [EUITagRuleType.Object];
    allowNullRule: boolean = false;

    /// set props from rule object
    parse(input: IUITagInput): IUITagResult | null {
        let rule = this.parseRule(input);
        if (rule == null) return null;
        return {
            props: {
                meta: rule,
            },
        };
    }
}

export type UIMetaBehavior = "inherit" | boolean;

export type WithUIMeta<M extends Record<string, any> = Record<string, any>> = {
    meta?: M | null;
};

type CreateUiMetaRuleOptions = {
    /** 是否對 boolean 做 flip（true->false, false->true） */
    flipBoolean?: boolean;
};

function createUiMetaRule<
    M extends Record<string, any>,
    K extends Extract<keyof M, string>,
>(
    rule: K,
    defaultBehavior: UIMetaBehavior = "inherit",
    options: CreateUiMetaRuleOptions = {},
) {
    const flipBoolean = options.flipBoolean ?? false;

    return (o: WithUIMeta<M>): UIMetaBehavior | M[K] => {
        const v = o?.meta?.[rule];

        const isUnset = v == null;
        if (isUnset) return defaultBehavior as any;

        if (flipBoolean) {
            if (v === true) return false as any;
            if (v === false) return true as any;
        }

        return v as any;
    };
}

/** 規則命中：不 flip */
export function uiMetaRuleMatch<
    M extends Record<string, any>,
    K extends Extract<keyof M, string>,
>(rule: K, defaultBehavior: UIMetaBehavior) {
    return createUiMetaRule<M, K>(rule, defaultBehavior, { flipBoolean: false });
}

/** 規則未命中：boolean 才 flip */
export function uiMetaRuleUnmatch<
    M extends Record<string, any>,
    K extends Extract<keyof M, string>,
>(rule: K, defaultBehavior: UIMetaBehavior) {
    return createUiMetaRule<M, K>(rule, defaultBehavior, { flipBoolean: true });
}
